/* Reading a lead's `receivedAt`: a UTC instant ("…T15:47:00Z"), a legacy
   zone-less timestamp, or a bare date ("2026-09-23") when the source knew only
   the day. A bare date must be treated as a calendar day, not midnight UTC —
   `Date.parse('2026-09-23')` is midnight UTC, which every US browser renders
   as 7:00 PM the evening before. That was every "7:00 PM" paid lead. */

export interface ReceivedAt { ms: number; dateOnly: boolean }

export function parseReceived(iso: string | null | undefined): ReceivedAt | null {
  if (!iso) return null;
  const s = iso.trim();
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dm) return { ms: new Date(+dm[1], +dm[2] - 1, +dm[3]).getTime(), dateOnly: true }; // local midnight
  const ms = Date.parse(s.replace(' ', 'T'));
  return Number.isNaN(ms) ? null : { ms, dateOnly: false };
}

/** Full day & time, e.g. "Mon, Jul 28, 2026 · 2:14 PM"; just the day when that is all we know. */
export function fmtDateTime(iso: string | null | undefined): string {
  const r = parseReceived(iso);
  if (!r) return iso || '—';
  const d = new Date(r.ms);
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  if (r.dateOnly) return day;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** Whole days since the lead arrived, by calendar day in the viewer's zone — so a lead from this morning is "Today" and one from last night is "Yesterday". */
export function daysSince(iso: string | null | undefined, now = Date.now()): number {
  const r = parseReceived(iso);
  if (!r) return 9999;
  const startOfDay = (ms: number) => { const d = new Date(ms); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(r.ms)) / 86_400_000));
}
